import { Link, useParams } from 'react-router-dom'
import WireframeBanner from './WireframeBanner'
import { PageDesignNote } from './designNotes'
import PageMasthead from '../../components/PageMasthead'
import RoleBadge from './components/RoleBadge'
import Button from '../../components/Button'
import { USERS } from './mockData'
import { Role, WireframeUser } from './types'
import { WIREFRAME_PATHS } from './routes'
import { initials } from '../../util/monogram'
import StatusPill from '../../components/badges/StatusPill'
import DetailRow from './components/DetailRow'

// The permission catalogue.
//
// These are the real capabilities from the live admin user page (UserDetailsForm +
// the UserToggle enum), grouped so a delivery manager can reason about them. In the
// shipped app each is an independent per-user checkbox; the RBAC model this prototype
// proposes derives them from a single role instead, which is why each permission
// records the roles that grant it. The page reads a user's role and renders the
// resulting grant/deny state — it never lets a reviewer toggle a permission directly.
interface Permission {
    label: string
    description: string
    roles: Role[] // roles that include this permission
}

interface PermissionGroup {
    name: string
    blurb: string
    permissions: Permission[]
}

const ALL: Role[] = ['Admin', 'Member', 'Viewer']
const ACTORS: Role[] = ['Admin', 'Member'] // anyone who can act, not just view

const PERMISSION_GROUPS: PermissionGroup[] = [
    {
        name: 'Moderation',
        blurb: 'Acting on comments in Moderate. Viewers can see comments but not change them.',
        permissions: [
            { label: 'Hide content', description: 'Hide severe or unwanted comments from public view.', roles: ACTORS },
            { label: 'Delete content', description: 'Permanently delete comments from the connected profile.', roles: ACTORS },
            { label: 'Label as spam', description: 'Mark comments as spam to sharpen filtering over time.', roles: ACTORS },
            { label: 'Block commenter', description: 'Block an account from the connected profile.', roles: ACTORS },
            { label: 'Mute commenter', description: 'Mute an account so its future comments are auto-hidden.', roles: ACTORS },
            { label: 'Manage shadow banning', description: 'Configure the repeat-offender threshold that triggers shadow banning.', roles: ['Admin'] },
            { label: 'View screenshots', description: 'View captured screenshots of moderated content.', roles: ALL }
        ]
    },
    {
        name: 'Engage',
        blurb: 'Replying to and engaging with the audience.',
        permissions: [
            { label: 'Reply to content', description: 'Reply to comments from within Arwen. Currently Meta only.', roles: ACTORS },
            { label: 'Like content', description: 'Like comments from within Arwen. Currently Facebook only.', roles: ACTORS },
            { label: 'Engage across profiles', description: 'Use Engage across every profile in scope, not one at a time.', roles: ACTORS }
        ]
    },
    {
        name: 'Insight & reporting',
        blurb: 'Reading the numbers behind the comments.',
        permissions: [
            { label: 'In-app report', description: 'View the in-app report for their team.', roles: ALL },
            { label: 'Homepage feeds', description: 'Create, manage and use homepage feeds.', roles: ACTORS },
            { label: 'Read / unread status', description: 'See and filter comments by read/unread status.', roles: ALL },
            { label: 'Ask Arwen', description: 'Ask Arwen questions about their comment data.', roles: ALL }
        ]
    },
    {
        name: 'Administration',
        blurb: 'Managing the organisation itself. Reserved for admins.',
        permissions: [
            { label: 'Manage users & access', description: 'Invite users, set roles and manage team access.', roles: ['Admin'] },
            { label: 'Manage classifiers & defaults', description: 'Edit classifiers and cross-profile default settings.', roles: ['Admin'] },
            { label: 'Access all teams & profiles', description: 'Org-wide access to every team and every profile.', roles: ['Admin'] }
        ]
    }
]

// A single permission row: label + description on the left, grant state on the right.
function PermissionRow({ permission, role }: { permission: Permission; role: Role }) {
    const granted = permission.roles.includes(role)
    return (
        <div className="flex items-start justify-between gap-4 py-2.5">
            <div className="min-w-0">
                <div className={`text-sm font-medium ${granted ? 'text-gray-900' : 'text-gray-400'}`}>
                    {permission.label}
                </div>
                <p className="mt-0.5 text-[11.5px] leading-relaxed text-gray-800">
                    {permission.description}
                </p>
            </div>
            {granted ? (
                <span className="inline-flex flex-none items-center gap-1.5 rounded-full bg-safe/10 px-2.5 py-0.5 text-xs font-semibold text-safe">
                    <span className="text-[11px]" aria-hidden="true">
                        ✓
                    </span>
                    Allowed
                </span>
            ) : (
                <span className="inline-flex flex-none items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-400">
                    Not available
                </span>
            )}
        </div>
    )
}

export default function UserDetailPage() {
    // The user tables pass the row's id in the path. Fall back to the first user so
    // the page still renders if someone deep-links the bare route.
    const { userId } = useParams<{ userId: string }>()
    const user: WireframeUser = USERS.find((u) => u.id === userId) ?? USERS[0]

    const isInvited = user.status === 'Invited'
    const grantedCount = PERMISSION_GROUPS.flatMap((g) => g.permissions).filter((p) =>
        p.roles.includes(user.role)
    ).length

    return (
        <div className="flex min-h-0 w-full flex-1 flex-col bg-gray-50">
            <WireframeBanner />

            <PageDesignNote type="rationale">
                A single user's detail and access across the organisation. Reached from any user
                table — org, team or profile.
            </PageDesignNote>

            <PageMasthead
                breadcrumb={
                    <>
                        <Link to={WIREFRAME_PATHS.orgList} className="text-arwen-700 hover:underline">
                            Organisations
                        </Link>{' '}
                        /{' '}
                        <Link to={WIREFRAME_PATHS.orgConfig} className="text-arwen-700 hover:underline">
                            Acme Corp
                        </Link>{' '}
                        /{' '}
                        <Link to={WIREFRAME_PATHS.orgUsers} className="text-arwen-700 hover:underline">
                            Users &amp; access
                        </Link>
                    </>
                }
                title={isInvited ? user.email : user.name}
                subtitle={isInvited ? 'Invitation pending' : user.email}
                actions={<Button text="Edit user" primary={false} type="button" size="small" />}
            />

            {/* Tab content */}
            <div className="flex-1 overflow-auto px-6 py-5">
                <div className="max-w-3xl">
                    {/* User summary */}
                    <div className="mb-4 rounded-lg border border-gray-200 bg-white">
                        <div className="flex items-center gap-3 border-b border-gray-100 px-4 py-3">
                            <div
                                className={`flex h-10 w-10 flex-none items-center justify-center rounded-full text-sm font-bold ${
                                    isInvited ? 'bg-gray-100 text-gray-400' : 'bg-arwen-100 text-arwen-700'
                                }`}
                                aria-hidden="true"
                            >
                                {initials(isInvited ? user.email : user.name)}
                            </div>
                            <div className="flex items-center gap-2">
                                <span
                                    className={`text-[15px] font-semibold ${isInvited ? 'text-gray-400' : 'text-gray-900'}`}
                                >
                                    {isInvited ? user.email : user.name}
                                </span>
                                {user.isKeyContact && (
                                    <span className="rounded-sm bg-arwen-50 px-1 py-0.5 text-[9px] font-bold uppercase tracking-wide text-arwen-600">
                                        Key Contact
                                    </span>
                                )}
                            </div>
                        </div>
                        <div className="divide-y divide-dashed divide-gray-100 px-4 py-1">
                            <DetailRow label="Email">{user.email}</DetailRow>
                            <DetailRow label="Role">
                                <RoleBadge role={user.role} />
                            </DetailRow>
                            <DetailRow label="Status">
                                {isInvited ? (
                                    <StatusPill tone="suspect">Invited</StatusPill>
                                ) : (
                                    <StatusPill tone="safe">Active</StatusPill>
                                )}
                            </DetailRow>
                            <DetailRow label="Teams">
                                {user.allTeams ? (
                                    <span className="inline-flex items-center rounded bg-arwen-50 px-1.5 py-0.5 text-[11px] font-semibold text-arwen-700">
                                        All teams
                                    </span>
                                ) : (
                                    <span className="flex flex-wrap gap-1">
                                        {user.teams.map((team) => (
                                            <span
                                                key={team}
                                                className="inline-flex items-center rounded bg-gray-100 px-1.5 py-0.5 text-[11px] text-gray-800"
                                            >
                                                {team}
                                            </span>
                                        ))}
                                    </span>
                                )}
                            </DetailRow>
                            <DetailRow label="Last active">
                                <span className="text-gray-800">{user.lastActive}</span>
                            </DetailRow>
                        </div>
                    </div>

                    {/* Permissions */}
                    <div className="rounded-lg border border-gray-200 bg-white">
                        <div className="flex items-center justify-between border-b border-gray-100 px-4 py-2.5">
                            <span className="text-[13px] font-semibold text-gray-900">
                                Permissions
                                <span className="ml-2 text-xs font-normal text-gray-800">
                                    {grantedCount} of {PERMISSION_GROUPS.flatMap((g) => g.permissions).length} allowed
                                </span>
                            </span>
                            <RoleBadge role={user.role} />
                        </div>

                        <div className="border-b border-gray-100 bg-arwen-50/60 px-4 py-2 text-[11.5px] text-arwen-700">
                            Permissions follow the <span className="font-semibold">{user.role}</span> role. Change the
                            role on the{' '}
                            <Link to={WIREFRAME_PATHS.orgUsers} className="font-semibold hover:underline">
                                Users &amp; access
                            </Link>{' '}
                            tab to adjust what this user can do.
                        </div>

                        <div className="px-4">
                            {PERMISSION_GROUPS.map((group) => (
                                <div key={group.name} className="border-b border-gray-50 py-3 last:border-b-0">
                                    <div className="mb-1">
                                        <h3 className="text-[10.5px] font-semibold uppercase tracking-wide text-gray-800">
                                            {group.name}
                                        </h3>
                                        <p className="mt-0.5 text-[11.5px] text-gray-800">{group.blurb}</p>
                                    </div>
                                    <div className="divide-y divide-dashed divide-gray-100">
                                        {group.permissions.map((permission) => (
                                            <PermissionRow
                                                key={permission.label}
                                                permission={permission}
                                                role={user.role}
                                            />
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}
